import { Menu, Notification } from '@carbon/icons-react';
import type { ReactNode } from 'react';
import { CdIconButton } from '../../atoms/CdIconButton';
import { CdIndicatorDot } from '../../atoms/CdIndicatorDot';
import { CdAccountMenu, type CdAccountMenuProps } from '../../molecules/CdAccountMenu';
import { CdLanguageSelect, type CdLanguageSelectProps } from '../../molecules/CdLanguageSelect';
import { cx } from '../../utils';
import styles from './CdAppHeader.module.scss';

export interface CdAppHeaderProps {
  /** Current section name shown in the bar (pages still render their own <h1>). */
  title: string;
  /** Shows the hamburger. The template shows it below `lg` or when the nav is closed. */
  onOpenNav?: () => void;
  showNavToggle?: boolean;
  unreadCount?: number;
  onOpenNotifications?: () => void;
  /** Signed-in user's avatar menu; omit when signed out. */
  account?: CdAccountMenuProps;
  /** UI language dropdown; shown whether or not the user is signed in. */
  language?: CdLanguageSelectProps;
  /** Extra global actions rendered before notifications. */
  actions?: ReactNode;
  className?: string;
}

export const CdAppHeader = ({
  title,
  onOpenNav,
  showNavToggle = false,
  unreadCount = 0,
  onOpenNotifications,
  account,
  language,
  actions,
  className,
}: CdAppHeaderProps) => {
  return (
    <header className={cx(styles.header, className)}>
      {showNavToggle && onOpenNav && (
        <CdIconButton
          label="Open navigation"
          icon={Menu}
          onClick={onOpenNav}
          align="bottom-start"
        />
      )}
      <p className={styles.title}>{title}</p>
      <div className={styles.actions}>
        {language && <CdLanguageSelect {...language} className={styles.language} />}
        {actions}
        {onOpenNotifications && (
          <span className={styles.notification}>
            <CdIconButton
              label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
              icon={Notification}
              onClick={onOpenNotifications}
              align="bottom-end"
            />
            {unreadCount > 0 && <CdIndicatorDot className={styles.badge} />}
          </span>
        )}
        {account && <CdAccountMenu {...account} />}
      </div>
    </header>
  );
};
